'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { isFilled } from '@prismicio/client';
import { PrismicText, PrismicRichText } from '@prismicio/react';
import TransitionLink from '@/components/TransitionLink';
import { PrismicNextImage } from '@prismicio/next';
import type { ImageField, RichTextField } from '@prismicio/client';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SplashButton from '@/components/SplashButton';
import { CATEGORY_ORDER } from '@/utils/integration-categories';

type IntegrationCard = {
  uid: string;
  title: string;
  description: string;
  image: ImageField;
  integration_mark: ImageField;
  integration_teaser: string;
  display_order: number;
  tags: string[];
  lang: string;
};

type IntegrationsGridSlice = {
  slice_type: string;
  slice_label: string | null;
  variation: string;
  primary: {
    kicker: string | null;
    heading: RichTextField;
    description: RichTextField;
  };
};

export type IntegrationsGridProps = {
  slice: IntegrationsGridSlice;
  context?: {
    integrations?: IntegrationCard[];
  };
  index?: number;
  slices?: unknown[];
};

const PAGE_SIZE = 9;


const IntegrationsGrid = ({ slice, context }: IntegrationsGridProps) => {
  const { kicker, heading, description } = slice.primary;
  const integrations: IntegrationCard[] = context?.integrations ?? [];

  // Group cards by tag (lowercase)
  const categoryMap = new Map<string, IntegrationCard[]>();

  for (const card of integrations) {
    for (const tag of card.tags) {
      const lower = tag.toLowerCase();
      if (!categoryMap.has(lower)) {
        categoryMap.set(lower, []);
      }
      categoryMap.get(lower)!.push(card);
    }
  }

  // Build tabs in defined order, only include categories that have cards
  const tabs: { label: string; cards: IntegrationCard[] }[] = [];

  for (const { tag, label } of CATEGORY_ORDER) {
    const cards = categoryMap.get(tag);
    if (cards && cards.length > 0) {
      tabs.push({ label, cards: [...cards].sort((a, b) => a.display_order - b.display_order) });
    }
  }

  const searchParams = useSearchParams();
  const tabParam = searchParams.get('tab');

  const [selectedTab, setSelectedTab] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    if (!tabParam) return;
    const idx = tabs.findIndex((t) =>
      t.label.toLowerCase() === tabParam.toLowerCase() ||
      CATEGORY_ORDER.find((c) => c.label === t.label)?.tag === tabParam
    );
    if (idx >= 0) setSelectedTab(idx);
  }, [tabParam]);
  const prevVisibleCount = useRef(PAGE_SIZE);
  const tabsRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const startScroll = (direction: 1 | -1) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const step = () => {
      el.scrollLeft += direction * 3;
      scrollRafRef.current = requestAnimationFrame(step);
    };
    scrollRafRef.current = requestAnimationFrame(step);
  };

  const stopScroll = () => {
    if (scrollRafRef.current !== null) {
      cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    }
  };

  const checkTabScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  useEffect(() => {
    checkTabScroll();
    const el = scrollContainerRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkTabScroll, { passive: true });
    window.addEventListener('resize', checkTabScroll);
    return () => {
      el.removeEventListener('scroll', checkTabScroll);
      window.removeEventListener('resize', checkTabScroll);
    };
  }, []);

  // Reset pagination and animation when switching tabs, scroll tab into view
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    prevVisibleCount.current = PAGE_SIZE;
    setAnimKey((k) => k + 1);

    const tabContainer = tabsRef.current;
    if (!tabContainer) return;
    const btn = tabContainer.children[selectedTab] as HTMLElement | undefined;
    if (btn) {
      btn.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedTab]);

  if (tabs.length === 0) return null;

  const currentCards = tabs[selectedTab]?.cards ?? [];
  const hasMore = currentCards.length > visibleCount;
  const visibleCards = currentCards.slice(0, visibleCount);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white py-20 pb-28"
      id="matrix"
    >
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-10 flex flex-col text-center sm:items-center">
          {isFilled.keyText(kicker) && (
            <h2 className="mb-10 text-sm uppercase opacity-40">{kicker}</h2>
          )}
          {isFilled.richText(heading) && (
            <h3 className="mb-2 max-w-4xl text-3xl font-normal leading-tight text-yellow-600 md:text-4xl md:font-light xl:text-5xl">
              <PrismicText field={heading} />
            </h3>
          )}
          {isFilled.richText(description) && (
            <div className="mt-2 w-full max-w-3xl text-base text-gray-600 sm:text-lg md:text-xl">
              <PrismicRichText
                field={description}
                components={{
                  hyperlink: ({ children, node }) => {
                    const target = 'target' in node.data ? node.data.target : undefined;
                    return (
                      <a
                        href={node.data.url}
                        target={target}
                        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                        className="text-blue-600 hover:underline hover:text-blue-800"
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              />
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="relative -mx-4 mb-10 sm:mx-auto sm:max-w-6xl">
          <div ref={scrollContainerRef} className="no-scrollbar overflow-x-auto">
            <div
              ref={tabsRef}
              className="flex gap-4 px-4 py-4 sm:px-5"
            >
              {tabs.map((tab, index) => (
                <div
                  key={tab.label}
                  className={`shrink-0 rounded-lg transition-colors ${
                    selectedTab === index ? 'bg-blue-600' : 'bg-zinc-900'
                  }`}
                >
                  <button
                    onClick={() => setSelectedTab(index)}
                    title={`${tab.label} NetSuite Integrations`}
                    className={`w-full origin-top-left whitespace-nowrap rounded-lg border px-4 py-2 sm:px-6 sm:py-3 transition-all text-base ${
                      selectedTab === index
                        ? '-translate-y-1 border-blue-600 bg-blue-50 font-bold text-blue-600'
                        : 'border-zinc-900 bg-white font-medium text-zinc-900 hover:-rotate-2'
                    }`}
                  >
                    {tab.label}
                  </button>
                </div>
              ))}
              <div className="w-5 shrink-0" aria-hidden="true" />
            </div>
          </div>
          {/* Fade edges + chevrons */}
          <div className={`absolute inset-y-0 -left-[60px] hidden sm:flex items-center transition-opacity duration-200 ${canScrollLeft ? 'opacity-100' : 'pointer-events-none opacity-0'}`}>
            <button
              onMouseEnter={() => startScroll(-1)}
              onMouseLeave={stopScroll}
              onFocus={() => startScroll(-1)}
              onBlur={stopScroll}
              onClick={() => scrollContainerRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}
              className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white"
              aria-label="Scroll tabs left"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
          </div>
          <div className={`pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent transition-opacity duration-200 ${canScrollLeft ? 'opacity-100' : 'opacity-0'}`} />
          <div className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent transition-opacity duration-200 ${canScrollRight ? 'opacity-100' : 'opacity-0'}`} />
          <div className={`absolute inset-y-0 -right-[60px] hidden sm:flex items-center transition-opacity duration-200 ${canScrollRight ? 'opacity-100' : 'pointer-events-none opacity-0'}`}>
            <button
              onMouseEnter={() => startScroll(1)}
              onMouseLeave={stopScroll}
              onFocus={() => startScroll(1)}
              onBlur={stopScroll}
              onClick={() => scrollContainerRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
              className="relative z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white"
              aria-label="Scroll tabs right"
            >
              <ChevronRight className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Hidden links for all inactive tabs — SEO only, no images loaded */}
        {tabs.map((tab, tabIndex) =>
          tabIndex === selectedTab ? null : (
            <div key={tab.label} className="hidden" aria-hidden="true">
              {tab.cards.map((card) => (
                <TransitionLink
                  key={card.uid}
                  href={`/${card.lang}/integrations/${card.uid}`}
                  tabIndex={-1}
                >
                  {card.title}
                </TransitionLink>
              ))}
            </div>
          )
        )}

        {/* Active tab cards grid */}
        <div className="relative">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visibleCards.map((card, cardIndex) => {
              const displayTitle = card.title
                .replace(/\s*\+\s*NetSuite\s+Integration$/i, '')
                .replace(/\s*NetSuite\s+Integration$/i, '');

              const isTeaser = hasMore && cardIndex >= visibleCount - 3;
              const isNew = cardIndex >= prevVisibleCount.current;
              const staggerIndex = isNew ? cardIndex - prevVisibleCount.current : cardIndex;

              return (
                <TransitionLink
                  key={`${animKey}-${card.uid}`}
                  href={`/${card.lang}/integrations/${card.uid}`}
                  style={{
                    animation: 'fadeInUp 0.3s ease-out both',
                    animationDelay: `${staggerIndex * 50}ms`,
                  }}
                  className={`group bg-gray-50 flex items-start gap-3 sm:gap-4 rounded-xl border border-gray-200 p-3 sm:p-5 transition-all duration-200 hover:border-gray-300 hover:shadow-md hover:bg-white ${
                    isTeaser ? 'pointer-events-none' : ''
                  }`}
                  tabIndex={isTeaser ? -1 : undefined}
                  aria-hidden={isTeaser ? true : undefined}
                >
                  <div className="flex h-16 w-16 sm:h-20 sm:w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border shadow-md transition-shadow group-hover:shadow-none p-1 bg-white group-hover:border-none transition-all">
                    {isFilled.image(card.integration_mark) ? (
                      <PrismicNextImage
                        field={card.integration_mark}
                        className="h-full w-full object-contain"
                        width={80}
                        height={80}
                      />
                    ) : isFilled.image(card.image) ? (
                      <PrismicNextImage
                        field={card.image}
                        className="h-full w-full object-contain"
                        width={80}
                        height={80}
                      />
                    ) : (
                      <svg className="h-8 w-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 leading-tight">
                      {displayTitle}
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      {card.integration_teaser || card.description}
                    </p>
                  </div>
                </TransitionLink>
              );
            })}
          </div>

          {hasMore && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-white via-white/90 to-transparent" />
          )}
        </div>

        {hasMore && (
          <div className="relative z-10 flex justify-center">
            <SplashButton
              as="button"
              text="Discover More"
              color="blue"
              icon="down"
              size="lg"
              className="min-w-[300px]"
              onClick={() => {
                prevVisibleCount.current = visibleCount;
                setVisibleCount((prev) => prev + PAGE_SIZE);
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default IntegrationsGrid;
